import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'
import { useFreePlan } from '@/hooks/use-free-plan'
import { FREE_DOMAIN_RULE, PAID_ANY_EMAIL } from '@/lib/free-domain'
import { FREE_PLAN, FREE_PLAN_EXCLUDES } from '@/lib/free-plan'
import { faqPage, jsonLdScript, type FaqEntry } from '@/lib/schema'

const URL = 'https://uptrack.app/free-uptime-monitoring'
const TITLE = 'Free Uptime Monitoring with 30-Second Checks | Uptrack'
const DESCRIPTION = `Free uptime monitoring for ${FREE_PLAN.monitors} websites or APIs, checked every ${FREE_PLAN.intervalSeconds} seconds. Email and chat alerts, a status page and ${FREE_PLAN.historyDays} days of history. No credit card.`

const faq: FaqEntry[] = [
  {
    question: 'Is the free plan really free?',
    answer: `Yes. It costs $0, needs no credit card and has no trial period. Commercial use is allowed. The number of free accounts is limited. When the limit is reached, new sign-ups see "Free plan is full" and can choose a paid plan instead.`,
  },
  {
    question: 'How many monitors do I get for free?',
    answer: `${FREE_PLAN.monitors} monitors, each checked every ${FREE_PLAN.intervalSeconds} seconds from one location, ${FREE_PLAN.location}. Each monitor can be an HTTP monitor or a keyword monitor.`,
  },
  {
    question: 'Who can use the free plan?',
    answer: `${FREE_DOMAIN_RULE} You can then monitor any site. ${PAID_ANY_EMAIL}`,
  },
  {
    question: 'How do I avoid false alarms with one location?',
    answer: `The free plan waits for consecutive failed checks before it alerts you, so one failed request does not start an alert. It cannot compare results between locations. For that, paid plans check from several locations, and several must confirm a failure.`,
  },
  {
    question: 'What does the free plan not include?',
    answer: `${FREE_PLAN_EXCLUDES[0].toUpperCase()}${FREE_PLAN_EXCLUDES.slice(1)}.`,
  },
  {
    question: 'Which free uptime monitoring tool is best?',
    answer: `It depends on what you need. UptimeRobot gives the most monitors for free (50, every 5 minutes). HetrixTools checks 15 monitors every minute. Uptrack checks ${FREE_PLAN.monitors} monitors every ${FREE_PLAN.intervalSeconds} seconds from one location. Uptime Kuma is free software that you host yourself. These limits were read on each pricing page in October 2026.`,
  },
  {
    question: 'Is free uptime monitoring enough for a business site?',
    answer: `For a few pages, often yes. Commercial use is allowed on the Uptrack free plan. One location cannot tell a regional problem from a real outage, so a site that earns money may need checks from several locations. Paid plans start at $3/mo.`,
  },
  {
    question: 'What happens when I need more?',
    answer: 'Pro costs $3/mo for 50 monitors at 30-second checks and 6 check areas, with a 30-day money-back guarantee.',
  },
]

export const Route = createFileRoute('/_site/free-uptime-monitoring')({
  component: FreeUptimeMonitoringPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:image', content: 'https://uptrack.app/og/free-uptime-monitoring.png' },
      { property: 'og:url', content: URL },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [jsonLdScript(faqPage(faq))],
  }),
})

const included = [
  {
    title: 'Checks every 30 seconds',
    desc: `All ${FREE_PLAN.monitors} monitors run at the same interval, from ${FREE_PLAN.location}. A check every 5 minutes can miss a short outage completely.`,
  },
  {
    title: 'Response checks, not only status codes',
    desc: 'A keyword monitor looks for text on the page. It finds the page that returns 200 but shows an error message.',
  },
  {
    title: 'Alerts by email and one chat channel',
    desc: 'Add one of Discord, Slack or Telegram next to email. Consecutive failed checks must happen before an alert goes out.',
  },
  {
    title: 'A public status page',
    desc: `You get ${FREE_PLAN.statusPages} status page, so customers can see current status and uptime data without asking you.`,
  },
  {
    title: 'Uptime and response time reports',
    desc: `The dashboard shows uptime, incidents and response time for the last ${FREE_PLAN.historyDays} days.`,
  },
  {
    title: 'Commercial use',
    desc: 'You can monitor a business site or a client site on the free plan.',
  },
]

// Free plan limits and first paid prices, from the "Competitor facts" in CLAUDE.md, checked on each
// company's pricing page on 2026-10-12. Change a row only after you read the page again.
const CHECKED = 'October 2026'
const freePlans = [
  { tool: 'Uptrack', to: null, monitors: String(FREE_PLAN.monitors), interval: `${FREE_PLAN.intervalSeconds} seconds`, note: 'One location. HTTP and keyword monitors. Needs a work email.' },
  { tool: 'UptimeRobot', to: '/compare/uptimerobot', monitors: '50', interval: '5 minutes', note: '30-second checks start on the Team plan, $41/mo ($35 yearly).' },
  { tool: 'HetrixTools', to: '/compare/hetrixtools', monitors: '15', interval: '1 minute', note: 'Every plan checks every minute.' },
  { tool: 'StatusCake', to: '/compare/statuscake', monitors: '10', interval: '5 minutes', note: '30-second checks start on the Business plan, $79.99/mo ($66.66 yearly).' },
  { tool: 'Better Stack', to: '/compare/betterstack', monitors: '10 and 10 heartbeats', interval: '3 minutes', note: '30-second checks need a responder seat, $34/mo ($29 yearly).' },
] as const

const picks = [
  { need: 'The most monitors', pick: 'UptimeRobot gives you 50, checked every 5 minutes.' },
  { need: 'The fastest checks', pick: `Uptrack checks ${FREE_PLAN.monitors} monitors every ${FREE_PLAN.intervalSeconds} seconds. HetrixTools checks 15 every minute.` },
  { need: 'No limit you set yourself', pick: 'Uptime Kuma is open source. You run it on your own server, so its checks come from that one place and you keep it running.' },
  { need: 'Heartbeats for cron jobs without paying', pick: 'Better Stack lists 10 heartbeats. At Uptrack heartbeat monitors need a paid plan.' },
]

const notForYou = [
  'You need more than 5 monitors for free. UptimeRobot gives you 50, at a 5-minute interval.',
  'You want to run the monitor yourself. Uptime Kuma is open source and self-hosted, and it has no plan limits.',
  'You need checks from several regions at no cost. The free plan uses one location.',
  'You need SSL, DNS, port, ping, heartbeat or browser checks. These are on paid plans.',
  'You do not have a work email address and do not want a paid plan.',
]

const steps = [
  { title: 'Create an account', desc: 'Use a work email address. You do not need a credit card.' },
  { title: 'Add the address to monitor', desc: 'Enter a URL. Add a keyword if the page can return 200 with an error message.' },
  { title: 'Connect an alert channel', desc: 'Email works at once. Add Discord, Slack or Telegram if your team reads chat first.' },
]

const H2 = 'text-2xl font-semibold tracking-tight md:text-3xl'
const TH = 'px-4 py-3 text-left text-sm font-semibold'
const TD = 'px-4 py-3 text-sm text-body'

function FreeUptimeMonitoringPage() {
  const free = useFreePlan()
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">Free uptime monitoring with 30-second checks</h1>
            <p className="mt-5 max-w-2xl text-lg text-body">
              Uptrack checks {FREE_PLAN.monitors} websites or APIs every {FREE_PLAN.intervalSeconds} seconds and alerts you when one fails. The
              free plan has no trial period and needs no credit card.
            </p>
            {free.isFull && (
              <p className="mt-4 max-w-2xl text-body">The free plan is full right now. New accounts can choose a paid plan from $3/mo.</p>
            )}
            {free.isClosed && (
              <p className="mt-4 max-w-2xl text-body">The free plan is closed at the moment. Paid plans start at $3/mo.</p>
            )}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <StartFreeButton />
              <Button size="lg" variant={free.isOpen ? 'outline' : 'default'} className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <PlanFinePrint className="mt-3" />
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className={H2}>What the free plan includes</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {included.map((item) => (
              <div key={item.title}>
                <h3 className="font-medium">{item.title}</h3>
                <p className="mt-2 text-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className={H2}>Free website monitoring tools compared</h2>
          <p className="mt-3 max-w-2xl text-body">
            A free plan is a choice between more monitors and faster checks. These values come from each company's public pricing page, checked in{' '}
            {CHECKED}. Plans change, so read the pricing page of a tool before you pick it. Read the{' '}
            <Link to="/blog/best-free-uptime-monitoring-tools" className="underline">
              full comparison of free tools
            </Link>
            .
          </p>
          <div className="mt-6 overflow-x-auto rounded-lg border border-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className={TH}>Tool</th>
                  <th className={TH}>Free monitors</th>
                  <th className={TH}>Fastest free check</th>
                  <th className={TH}>Note</th>
                </tr>
              </thead>
              <tbody>
                {freePlans.map((row) => (
                  <tr key={row.tool} className="border-b border-border last:border-0">
                    <td className={TD}>
                      {row.to ? (
                        <Link to={row.to} className="underline">
                          {row.tool}
                        </Link>
                      ) : (
                        row.tool
                      )}
                    </td>
                    <td className={`${TD} num`}>{row.monitors}</td>
                    <td className={`${TD} num`}>{row.interval}</td>
                    <td className={TD}>{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
            Freshping shut down on 2026-03-06, so it is not in the table. Uptime Kuma is not in it either: it is self-hosted, so it has no plan limits.
          </p>
          <h3 className="mt-10 text-xl font-semibold">Which one fits</h3>
          <ul className="mt-4 max-w-3xl divide-y divide-border border-y border-border">
            {picks.map((p) => (
              <li key={p.need} className="grid gap-x-8 gap-y-1 py-4 md:grid-cols-12">
                <span className="font-medium md:col-span-4">{p.need}</span>
                <span className="text-body md:col-span-8">{p.pick}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto grid gap-12 px-4 py-14 md:py-20 lg:grid-cols-2">
          <div>
            <h2 className={H2}>Set it up</h2>
            <ol className="mt-6 divide-y divide-border border-y border-border">
              {steps.map((step, i) => (
                <li key={step.title} className="flex gap-4 py-5">
                  <span className="num w-5 text-sm text-muted-foreground">{i + 1}</span>
                  <div>
                    <h3 className="font-medium">{step.title}</h3>
                    <p className="mt-1 text-body">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-body">
              New to this? Read{' '}
              <Link to="/blog/what-is-uptime-monitoring" className="underline">
                what uptime monitoring is
              </Link>
              , or test one address now with the{' '}
              <Link to="/tools/is-website-down" className="underline">
                website down checker
              </Link>
              .
            </p>
          </div>
          <div>
            <h2 className={H2}>This plan is not for you if</h2>
            <ul className="mt-6 list-disc space-y-3 pl-6 text-body">
              {notForYou.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto max-w-3xl px-4 py-14 md:py-20">
          <h2 className={H2}>Frequently asked questions</h2>
          <div className="mt-8 space-y-6">
            {faq.map((entry) => (
              <div key={entry.question}>
                <h3 className="font-medium">{entry.question}</h3>
                <p className="mt-1 text-body">{entry.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
